import React from "react";
import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";
import type { MiContenidoMetricsExportData } from "../../miContenidoExportData";
import { showProgramPageChannelNames } from "../../programPages/sortProgramPagesForExport";
import {
  fmtEpisodeDate,
  fmtMilesViews,
} from "../commercial/buildCommercialViewsData";
import {
  METRICS_PDF_COVER_PADDING_BOTTOM,
  METRICS_PDF_COVER_PADDING_TOP,
  METRICS_PDF_PAGE_PADDING_HORIZONTAL,
  METRICS_PDF_PAGE_SIZE,
} from "../constants";
import {
  formatFooterDate,
  formatLongDate,
  formatPeriodLabel,
  formatShortPeriodLabel,
} from "../formatHelpers";
import { MetricsPdfCoverPage } from "../MetricsPdfCoverPage";
import { MetricsPdfDeckPage } from "../MetricsPdfDeckPage";
import {
  MetricsPdfGridTable,
  type GridColumn,
  type GridRow,
} from "../MetricsPdfGridTable";
import { sanitizePdfDisplayText } from "../sanitizePdfDisplayText";
import { syncPdfTheme, usePdfTheme } from "../PdfThemeContext";
import { MetricsPdfPlatformBreakdownLogo } from "../MetricsPdfPlatformIcons";
import {
  PDF_CARD_BORDER_WIDTH,
  PDF_CARD_RADIUS,
  PDF_FONT,
  PDF_FONT_FAMILY,
  type PdfColorMode,
} from "../pdfTheme";
import { registerMetricsPdfFonts } from "../registerMetricsPdfFonts";
import {
  buildPlatformReportData,
  type PlatformReportContentInput,
  type PlatformReportContentItem,
  type PlatformSummaryMetric,
} from "./buildPlatformReportData";
import type { PlatformReportId } from "./platformReportPlatforms";

registerMetricsPdfFonts();

function platformFootnote(id: PlatformReportId): string {
  switch (id) {
    case "youtube_videos":
      return i18n.t("metricsPdf:footnoteYoutubeVideos");
    case "youtube_shorts":
      return i18n.t("metricsPdf:footnoteYoutubeShorts");
    case "instagram":
      return i18n.t("metricsPdf:footnoteInstagram");
    case "tiktok":
      return i18n.t("metricsPdf:footnoteTiktok");
    case "facebook":
      return i18n.t("metricsPdf:footnoteFacebook");
  }
}

function contentColumns(): GridColumn[] {
  return [
    { key: "label", label: i18n.t("metricsPdf:colContent"), flex: 2.4 },
    { key: "program", label: i18n.t("metricsPdf:colProgram"), flex: 1.3 },
    { key: "date", label: i18n.t("metricsPdf:colDate"), width: 54 },
    { key: "views", label: i18n.t("metricsPdf:views"), flex: 0.7, align: "right" },
    {
      key: "interactions",
      label: i18n.t("metricsPdf:interactions"),
      flex: 0.95,
      align: "right",
    },
    { key: "link", label: i18n.t("metricsPdf:colLink"), width: 36, align: "right" },
  ];
}

function fmtCount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "—";
  return Math.round(n).toLocaleString("es-CL");
}

function joinProgramNames(names: readonly string[]): string {
  return names
    .map((name) => sanitizePdfDisplayText(name))
    .filter((name) => name !== "—")
    .join(" · ");
}

function coverCopy(pickedProgramNames: readonly string[] | undefined): {
  title: string;
  subtitle?: string;
} {
  if (!pickedProgramNames || pickedProgramNames.length === 0) {
    return { title: i18n.t("metricsPdf:platformProgramsReport") };
  }
  const list = joinProgramNames(pickedProgramNames);
  if (!list) return { title: i18n.t("metricsPdf:platformProgramsReport") };
  return { title: i18n.t("metricsPdf:platformProgramsReport"), subtitle: list };
}

function programLabel(name: string, channelName: string | undefined, showChannel: boolean): string {
  const clean = sanitizePdfDisplayText(name);
  const channel = showChannel && channelName ? sanitizePdfDisplayText(channelName) : "";
  return channel ? `${clean} · ${channel}` : clean;
}

function summaryStats(): Array<{
  label: string;
  value: (metric: {
    contentCount: number;
    totalInteractions: number;
    avgViews: number;
    avgInteractions: number;
  }) => string;
}> {
  return [
    { label: i18n.t("metricsPdf:statContents"), value: (metric) => fmtCount(metric.contentCount) },
    {
      label: i18n.t("metricsPdf:interactions"),
      value: (metric) => fmtMilesViews(metric.totalInteractions),
    },
    {
      label: i18n.t("metricsPdf:avgViewsPerContent"),
      value: (metric) => fmtMilesViews(metric.avgViews),
    },
    {
      label: i18n.t("metricsPdf:avgInteractionsPerContent"),
      value: (metric) => fmtMilesViews(metric.avgInteractions),
    },
  ];
}

function platformRows(platforms: PlatformSummaryMetric[]): PlatformSummaryMetric[][] {
  const rows: PlatformSummaryMetric[][] = [];
  for (let index = 0; index < platforms.length; index += 2) {
    rows.push(platforms.slice(index, index + 2));
  }
  return rows;
}

function sumPlatforms(platforms: PlatformSummaryMetric[]): {
  contentCount: number;
  totalViews: number;
  avgViews: number;
  totalInteractions: number;
  avgInteractions: number;
} {
  const contentCount = platforms.reduce((sum, platform) => sum + platform.contentCount, 0);
  const totalViews = platforms.reduce((sum, platform) => sum + platform.totalViews, 0);
  const totalInteractions = platforms.reduce(
    (sum, platform) => sum + platform.totalInteractions,
    0,
  );
  return {
    contentCount,
    totalViews,
    totalInteractions,
    avgViews: contentCount > 0 ? Math.round(totalViews / contentCount) : 0,
    avgInteractions: contentCount > 0 ? Math.round(totalInteractions / contentCount) : 0,
  };
}

function PlatformSummaryCards({
  platforms,
}: {
  platforms: PlatformSummaryMetric[];
}): React.ReactElement {
  const theme = usePdfTheme();
  const total = sumPlatforms(platforms);
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        totalCard: {
          borderWidth: PDF_CARD_BORDER_WIDTH,
          borderColor: theme.cardBorder,
          borderRadius: PDF_CARD_RADIUS,
          backgroundColor: theme.cardBg,
          paddingHorizontal: 18,
          paddingVertical: 10,
          marginBottom: 10,
        },
        totalTitle: {
          fontSize: 12,
          fontWeight: PDF_FONT.semibold,
          color: theme.textMutedAlt,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          fontFamily: PDF_FONT_FAMILY,
        },
        totalBody: {
          flexDirection: "row",
          alignItems: "flex-end",
          marginTop: 8,
        },
        totalViewsWrap: {
          width: 240,
          marginRight: 18,
        },
        views: {
          fontSize: 22,
          fontWeight: PDF_FONT.bold,
          color: theme.brandPrimary,
          fontFamily: PDF_FONT_FAMILY,
          marginBottom: 2,
        },
        viewsLabel: {
          fontSize: 10,
          color: theme.labelMuted,
          fontWeight: PDF_FONT.medium,
          fontFamily: PDF_FONT_FAMILY,
        },
        totalStats: {
          flex: 1,
          flexDirection: "row",
        },
        totalStat: {
          flex: 1,
          minWidth: 0,
          marginRight: 12,
        },
        totalStatLast: {
          marginRight: 0,
        },
        gridRow: {
          flexDirection: "row",
          marginBottom: 10,
        },
        gridRowLast: {
          marginBottom: 0,
        },
        card: {
          flex: 1,
          minWidth: 0,
          borderWidth: PDF_CARD_BORDER_WIDTH,
          borderColor: theme.cardBorder,
          borderRadius: PDF_CARD_RADIUS,
          backgroundColor: theme.cardBg,
          paddingHorizontal: 14,
          paddingTop: 10,
          paddingBottom: 10,
          marginRight: 10,
        },
        cardLast: {
          marginRight: 0,
        },
        titleRow: {
          flexDirection: "row",
          alignItems: "center",
        },
        cardSpacer: {
          flex: 1,
        },
        title: {
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          fontWeight: PDF_FONT.semibold,
          color: theme.textMutedAlt,
          textTransform: "uppercase",
          letterSpacing: 0.4,
          fontFamily: PDF_FONT_FAMILY,
        },
        cardViews: {
          fontSize: 22,
          fontWeight: PDF_FONT.bold,
          color: theme.brandPrimary,
          marginTop: 6,
          marginBottom: 2,
          fontFamily: PDF_FONT_FAMILY,
        },
        cardViewsLabel: {
          fontSize: 10,
          color: theme.labelMuted,
          fontWeight: PDF_FONT.medium,
          marginBottom: 4,
          fontFamily: PDF_FONT_FAMILY,
        },
        statGrid: {
          flexDirection: "row",
          flexWrap: "wrap",
        },
        statCell: {
          width: "50%",
          paddingRight: 8,
          marginTop: 6,
        },
        statLabel: {
          fontSize: 9,
          color: theme.textMutedAlt,
          fontFamily: PDF_FONT_FAMILY,
        },
        statValue: {
          fontSize: 13,
          fontWeight: PDF_FONT.bold,
          color: theme.brandPrimary,
          marginTop: 2,
          fontFamily: PDF_FONT_FAMILY,
        },
      }),
    [theme],
  );

  return (
    <View>
      <View style={styles.totalCard} wrap={false}>
        <Text style={styles.totalTitle}>{i18n.t("metricsPdf:total")}</Text>
        <View style={styles.totalBody}>
          <View style={styles.totalViewsWrap}>
            <Text style={styles.views}>{fmtMilesViews(total.totalViews)}</Text>
            <Text style={styles.viewsLabel}>Views</Text>
          </View>
          <View style={styles.totalStats}>
            {summaryStats().map((stat, index) => (
              <View
                key={stat.label}
                style={[
                  styles.totalStat,
                  ...(index === summaryStats().length - 1 ? [styles.totalStatLast] : []),
                ]}
              >
                <Text style={styles.statLabel}>{stat.label}</Text>
                <Text style={styles.statValue}>{stat.value(total)}</Text>
              </View>
            ))}
          </View>
        </View>
      </View>
      <View>
        {platformRows(platforms).map((row, rowIndex, rows) => (
          <View
            key={row.map((platform) => platform.id).join("-")}
            style={[
              styles.gridRow,
              ...(rowIndex === rows.length - 1 ? [styles.gridRowLast] : []),
            ]}
          >
            {row.map((platform, index) => (
              <View
                key={platform.id}
                style={[
                  styles.card,
                  ...(index === row.length - 1 ? [styles.cardLast] : []),
                ]}
                wrap={false}
              >
                <View style={styles.titleRow}>
                  <MetricsPdfPlatformBreakdownLogo platformKey={platform.id} marginRight={6} />
                  <Text style={styles.title}>{platform.label}</Text>
                </View>
                <Text style={styles.cardViews}>{fmtMilesViews(platform.totalViews)}</Text>
                <Text style={styles.cardViewsLabel}>Views</Text>
                <View style={styles.statGrid}>
                  {summaryStats().map((stat) => (
                    <View key={stat.label} style={styles.statCell}>
                      <Text style={styles.statLabel}>{stat.label}</Text>
                      <Text style={styles.statValue}>{stat.value(platform)}</Text>
                    </View>
                  ))}
                </View>
              </View>
            ))}
            {row.length === 1 ? <View style={styles.cardSpacer} /> : null}
          </View>
        ))}
      </View>
    </View>
  );
}

function clipTitle(title: string): string {
  const clean = sanitizePdfDisplayText(title, i18n.t("metricsPdf:untitled"));
  if (clean.length <= 72) return clean;
  return `${clean.slice(0, 71).trimEnd()}...`;
}

function contentRows(
  items: PlatformReportContentItem[],
  showChannel: boolean,
): GridRow[] {
  return items.map((item) => ({
    key: item.id,
    label: clipTitle(item.title),
    values: {
      program: programLabel(item.programName, item.channelName, showChannel),
      date: fmtEpisodeDate(item.publishedAt),
      views: fmtMilesViews(item.views),
      interactions: fmtMilesViews(item.interactions),
      link: item.url ? i18n.t("metricsPdf:viewLink") : "—",
    },
    ...(item.url ? { hrefs: { link: item.url } } : {}),
  }));
}

function CoverPage({
  branding,
  periodLabel,
  audienceDemoPlaceholder,
  pickedProgramNames,
}: {
  branding: MiContenidoMetricsExportData["branding"];
  periodLabel: string;
  audienceDemoPlaceholder?: boolean;
  pickedProgramNames?: readonly string[];
}): React.ReactElement {
  const theme = usePdfTheme();
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        coverPage: {
          flexDirection: "column",
          backgroundColor: theme.pageBg,
          paddingTop: METRICS_PDF_COVER_PADDING_TOP,
          paddingBottom: METRICS_PDF_COVER_PADDING_BOTTOM,
          paddingHorizontal: METRICS_PDF_PAGE_PADDING_HORIZONTAL,
          fontFamily: PDF_FONT_FAMILY,
          fontSize: 13,
          color: theme.textBody,
        },
      }),
    [theme],
  );

  const cover = coverCopy(pickedProgramNames);

  return (
    <Page size={METRICS_PDF_PAGE_SIZE} style={styles.coverPage} wrap={false}>
      <MetricsPdfCoverPage
        branding={branding}
        periodLabel={periodLabel}
        generated={formatLongDate()}
        title={cover.title}
        {...(cover.subtitle ? { subtitle: cover.subtitle } : {})}
        {...(branding.podcastCoverUrl || branding.companyLogoUrl
          ? { coverImageUrl: branding.podcastCoverUrl || branding.companyLogoUrl }
          : {})}
        {...(audienceDemoPlaceholder ? { audienceDemoPlaceholder: true } : {})}
      />
    </Page>
  );
}

function EmptyNote({ text }: { text: string }): React.ReactElement {
  const theme = usePdfTheme();
  const styles = React.useMemo(
    () =>
      StyleSheet.create({
        text: { fontSize: 13, color: theme.textMutedAlt, fontFamily: PDF_FONT_FAMILY },
      }),
    [theme],
  );
  return <Text style={styles.text}>{text}</Text>;
}

type Props = {
  data: MiContenidoMetricsExportData;
  platformIds: readonly PlatformReportId[];
  content?: PlatformReportContentInput;
  /** Set when the report covers a subset of the channel's programs. */
  pickedProgramNames?: readonly string[];
  colorMode?: PdfColorMode;
  periodStart?: string;
  periodEnd?: string;
};

export function PlatformReportPdfDocument({
  data,
  platformIds,
  content,
  pickedProgramNames,
  colorMode = "light",
  periodStart,
  periodEnd,
}: Props): React.ReactElement {
  const { t } = useTranslation("metricsPdf");
  syncPdfTheme(colorMode);

  const { branding, period } = data;
  const startDate = periodStart || period.startDate;
  const endDate = periodEnd || period.endDate;
  const report = buildPlatformReportData(data, platformIds, content);
  const periodLabel = formatShortPeriodLabel(startDate, endDate);
  const showChannelNames = showProgramPageChannelNames(data.programPages);
  const deckProps = { branding, generated: formatFooterDate() };
  const summaryFooter = t("summaryFooter");
  const pickedLabel =
    pickedProgramNames && pickedProgramNames.length > 0
      ? joinProgramNames(pickedProgramNames)
      : "";

  return (
    <Document>
      <CoverPage
        branding={branding}
        periodLabel={formatPeriodLabel(startDate, endDate)}
        {...(pickedProgramNames ? { pickedProgramNames } : {})}
        {...(data.audienceDemoPlaceholder ? { audienceDemoPlaceholder: true } : {})}
      />

      {platformIds.length === 0 ? (
        <MetricsPdfDeckPage
          {...deckProps}
          headerTitle={t("byPlatform")}
          headerPeriodLabel={periodLabel}
        >
          <EmptyNote text={t("selectPlatform")} />
        </MetricsPdfDeckPage>
      ) : report.summary.length === 0 ? (
        <MetricsPdfDeckPage
          {...deckProps}
          headerTitle={t("metricsByPlatform")}
          headerPeriodLabel={periodLabel}
        >
          <EmptyNote text={t("noProgramActivity")} />
        </MetricsPdfDeckPage>
      ) : (
        <MetricsPdfDeckPage
          {...deckProps}
          headerTitle={t("metricsByPlatform")}
          headerPeriodLabel={periodLabel}
          {...(pickedLabel ? { headerSubtitle: pickedLabel } : {})}
          footerNote={summaryFooter}
          contentPaddingTop={8}
          contentPaddingBottom={6}
        >
          <PlatformSummaryCards platforms={report.summary} />
        </MetricsPdfDeckPage>
      )}

      {report.contentPages.map((page) => (
        <MetricsPdfDeckPage
          key={`${page.platformId}-${page.pageIndex}`}
          {...deckProps}
          headerTitle={page.label}
          headerPeriodLabel={periodLabel}
          headerSubtitle={
            page.pageCount > 1
              ? t("contentsPage", { page: page.pageIndex, total: page.pageCount })
              : t("contents")
          }
          footerNote={`${platformFootnote(page.platformId)}${
            page.truncated ? t("recentContents") : ""
          }`}
        >
          <MetricsPdfGridTable
            columns={contentColumns()}
            rows={contentRows(page.items, showChannelNames)}
          />
        </MetricsPdfDeckPage>
      ))}
    </Document>
  );
}
